import React, { useState } from 'react';
import type { UserItem } from '../../api/users';
import type { UserActionMutations } from './useUserActionMutations';

type BulkVerb = 'suspend' | 'unsuspend' | 'delete';

interface BulkActionBarProps {
  selected: Set<string>;
  users: UserItem[];
  mutations: UserActionMutations;
  onClear: () => void;
}

// #583 — bulk-action bar shown when at least one row is checked. All
// three verbs share one reason prompt (looped sequentially so a mid-run
// server failure halts and reports partial progress). Skips no-op rows
// (already-suspended on Suspend, already-active on Unsuspend) rather
// than issuing a call that the server would 409.

async function runBulk(
  verb: BulkVerb,
  targets: UserItem[],
  reason: string,
  mutations: UserActionMutations,
  setProgress: (v: { done: number; total: number } | null) => void,
): Promise<{ done: number; total: number; failed?: string }> {
  setProgress({ done: 0, total: targets.length });
  for (let i = 0; i < targets.length; i++) {
    const t = targets[i];
    try {
      if (verb === 'suspend') await mutations.suspendAsync(t.userId, reason);
      else if (verb === 'unsuspend') await mutations.unsuspendAsync(t.userId, reason);
      else await mutations.deleteAsync(t.userId, reason);
      setProgress({ done: i + 1, total: targets.length });
    } catch (err) {
      setProgress(null);
      return { done: i, total: targets.length, failed: (err as Error).message };
    }
  }
  setProgress(null);
  return { done: targets.length, total: targets.length };
}

function filterTargets(verb: BulkVerb, selected: Set<string>, users: UserItem[]): UserItem[] {
  const inSelection = users.filter((u) => selected.has(u.userId));
  if (verb === 'suspend') return inSelection.filter((u) => !u.suspended);
  if (verb === 'unsuspend') return inSelection.filter((u) => u.suspended);
  return inSelection;
}

async function handleBulkClick(
  verb: BulkVerb,
  selected: Set<string>,
  users: UserItem[],
  mutations: UserActionMutations,
  setProgress: (v: { done: number; total: number } | null) => void,
  onClear: () => void,
): Promise<void> {
  const targets = filterTargets(verb, selected, users);
  if (targets.length === 0) {
    window.alert(`No selected user needs ${verb}. Nothing to do.`);
    return;
  }
  if (verb === 'delete') {
    if (!window.confirm(`DELETE ${targets.length} user${targets.length === 1 ? '' : 's'}?\n\nThis is irreversible.`)) return;
  }
  const label = verb === 'suspend' ? 'Suspend' : verb === 'unsuspend' ? 'Unsuspend' : 'Delete';
  const raw = window.prompt(`${label} ${targets.length} user${targets.length === 1 ? '' : 's'}?\n\nReason (applies to all, required for audit log):`);
  const reason = raw && raw.trim() ? raw.trim() : null;
  if (!reason) return;
  const result = await runBulk(verb, targets, reason, mutations, setProgress);
  if (result.failed) {
    window.alert(`${label} bulk halted after ${result.done}/${result.total} — first failure: ${result.failed}`);
  } else {
    window.alert(`${label}: ${result.done}/${result.total} complete.`);
    onClear();
  }
}

const BTN =
  'px-3 py-1.5 text-sm font-medium rounded border border-border hover:bg-surface-subtle disabled:opacity-50 disabled:cursor-not-allowed';

export function BulkActionBar({ selected, users, mutations, onClear }: BulkActionBarProps): React.ReactElement | null {
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);
  if (selected.size === 0) return null;
  const busy = progress !== null;
  const label = busy ? `Running… ${progress!.done}/${progress!.total}` : `${selected.size} selected`;
  return (
    <div className="flex items-center gap-3 bg-brand-50 border border-brand-200 rounded-md px-4 py-3">
      <span className="text-sm font-medium text-brand-700">{label}</span>
      <div className="flex gap-2 ml-auto">
        <button type="button" disabled={busy} className={BTN} onClick={() => void handleBulkClick('suspend', selected, users, mutations, setProgress, onClear)}>
          Suspend selected
        </button>
        <button type="button" disabled={busy} className={BTN} onClick={() => void handleBulkClick('unsuspend', selected, users, mutations, setProgress, onClear)}>
          Unsuspend selected
        </button>
        <button type="button" disabled={busy} className={`${BTN} text-red-700 hover:bg-red-50`} onClick={() => void handleBulkClick('delete', selected, users, mutations, setProgress, onClear)}>
          Remove selected
        </button>
        <button type="button" disabled={busy} className={BTN} onClick={onClear}>
          Clear
        </button>
      </div>
    </div>
  );
}
